<template>
    <div class="card" :class="[
      type && `card-${type}`, 
      isDarkMode ? 'dark-card' : ''
    ]">
      <div class="card-image" v-if="$slots.image">
        <slot name="image"></slot>
      </div>
      <div class="card-header" v-if="$slots.header || title" :class="[headerClasses, isDarkMode ? 'dark-header' : '']">
        <slot name="header">
          <h4 class="card-title">{{title}}</h4>
          <p class="card-category" v-if="subTitle">{{subTitle}}</p>
        </slot>
      </div>
      <div class="card-body" v-if="$slots.default" :class="[bodyClasses, isDarkMode ? 'dark-body' : '']">
        <slot></slot>
      </div>
      <slot name="raw-content"></slot>
      <div class="card-footer" :class="[footerClasses, isDarkMode ? 'dark-footer' : '']" v-if="$slots.footer">
        <slot name="footer"></slot>
      </div>
    </div>
  </template>
  <script>
    import { useThemeStore } from '@stores/themeStore';
    import { storeToRefs } from 'pinia';
    
    export default {
      name: "card",
      props: {
        title: {
          type: String,
          description: "Card title"
        },
        subTitle: {
          type: String,
          description: "Card subtitle"
        },
        type: {
          type: String,
          description: "Card type (e.g primary/danger etc)"
        },
        headerClasses: {
          type: [String, Object, Array],
          description: "Card header css classes"
        },
        bodyClasses: {
          type: [String, Object, Array],
          description: "Card body css classes"
        },
        footerClasses: {
          type: [String, Object, Array],
          description: "Card footer css classes"
        }
      },
      setup() {
        const themeStore = useThemeStore();
        const { isDarkMode } = storeToRefs(themeStore);
        
        return {
          isDarkMode
        };
      }
    };
  </script>
  <style scoped>
/* Canonical Card — uses --adm-* tokens directly (see tokens.css). */
.card {
  border-radius: var(--adm-radius);
  overflow: hidden;
  margin: 0.1em;
  background-color: var(--adm-card);
  color: var(--adm-text-strong);
  border: 1px solid var(--adm-border);
  box-shadow: var(--adm-shadow-sm);
  transition: transform 0.18s ease, box-shadow 0.18s ease, background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease;
}

.dark-card {
  box-shadow: var(--adm-shadow-sm);
  color: var(--adm-text-strong);
}

.card-header {
  padding: 15px 18px;
  border-bottom: 1px solid var(--adm-border);
  transition: border-color 0.3s ease;
}

.dark-header {
  border-bottom-color: var(--adm-border);
}

.card-title {
  margin: 0;
  font-family: 'Sora', sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--adm-text-strong);
}

.card-category {
  margin: 4px 0 0;
  font-size: 0.74rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--adm-muted);
}

.card-body {
  padding: 18px;
}

.dark-body {
  color: var(--adm-text-strong);
}

.card-footer {
  padding: 12px 18px;
  border-top: 1px solid var(--adm-border);
  transition: border-color 0.3s ease;
}

.dark-footer {
  border-top-color: var(--adm-border);
}

.card h3 {
  margin-top: 0.5em;
  font-size: 1.2em;
  font-family: 'Sora', sans-serif;
  letter-spacing: -0.01em;
  color: var(--adm-text-strong);
}

.dark-card h3 {
  color: var(--adm-text-strong);
}

.card p {
  color: var(--adm-text);
}

.dark-card p {
  color: var(--adm-text);
}

.btn {
  border-radius: var(--adm-radius-sm);
  padding: 0.5em 1em;
  border: 1px solid var(--adm-border);
  background: var(--adm-card-head);
  color: var(--adm-text-strong);
  cursor: pointer;
  transition: all 0.18s ease;
}

.btn:hover {
  border-color: var(--adm-accent);
  color: var(--adm-accent);
}

/* Card type variations */
.card-primary {
  border-top: 3px solid var(--adm-accent);
}

.card-success {
  border-top: 3px solid var(--adm-success);
}

.card-danger {
  border-top: 3px solid var(--adm-danger);
}

.card-warning {
  border-top: 3px solid var(--adm-warning);
}

.card-info {
  border-top: 3px solid var(--adm-info);
}

.dark-card.card-primary {
  border-top-color: var(--adm-accent);
}

.dark-card.card-success {
  border-top-color: var(--adm-success);
}

.dark-card.card-danger {
  border-top-color: var(--adm-danger);
}

.dark-card.card-warning {
  border-top-color: var(--adm-warning);
}

.dark-card.card-info {
  border-top-color: var(--adm-info);
}
  </style>
